/* =============================================================================
   Zeichen, die keine Handschrift-Schrift enthält

   Keine der mitgelieferten Schriften hat π, √, ≤, ≠, ·, ° oder griechische
   Buchstaben. Bisher fielen sie deshalb ersatzlos weg. Hier werden sie
   nachgebaut — und zwar möglichst aus den Zeichen der Schrift selbst, damit
   sie zur Hand passen:

     ≠  =  „=“ der Schrift + Schrägstrich      ±  =  „+“ + Strich darunter
     ≤  =  „<“ der Schrift + Strich darunter   ≈  =  zweimal „~“
     ·  =  „.“ der Schrift, auf die Mitte gehoben
     °  =  kleines „o“ der Schrift, hochgestellt
     ∅  =  „0“ + Schrägstrich                  ℝ  =  „R“ + zweiter Strich

   Was sich nicht zusammensetzen lässt (π, α, Pfeile, ∈ …), wird als Strich
   gezeichnet: Polylinien in Einheiten der x-Höhe, Grundlinie bei 0, nach oben
   positiv. Beim Setzen bekommen sie die Strichstärke der Schrift und einen
   leichten Zitter (darstellen.js), damit sie nicht wie gedruckt wirken.

   Hat eine Schrift das Zeichen doch (etwa eine selbst gezeichnete Handschrift
   mit π), gewinnt immer die Schrift — siehe UNICODE unten und layout.js.
   ========================================================================== */

/* Welches Unicode-Zeichen ein Symbol meint — wenn die Schrift es hat, wird
   es direkt benutzt. */
export const UNICODE = {
  pi: "π", alpha: "α", beta: "β", gamma: "γ", delta: "δ", epsilon: "ε",
  theta: "θ", lambda: "λ", mu: "μ", rho: "ρ", sigma: "σ", tau: "τ",
  phi: "φ", omega: "ω", Omega: "Ω", Sigma: "Σ", Delta: "Δ",
  le: "≤", ge: "≥", ne: "≠", approx: "≈", equiv: "≡", cong: "≅",
  pm: "±", mp: "∓", times: "×", cdot: "·", infty: "∞", deg: "°",
  to: "→", from: "←", leftrightarrow: "↔", Rightarrow: "⇒",
  Leftarrow: "⇐", Leftrightarrow: "⇔", in: "∈", notin: "∉",
  subset: "⊂", subseteq: "⊆", cup: "∪", cap: "∩", perp: "⊥",
  parallel: "∥", angle: "∠", empty: "∅", prime: "′",
  bbR: "ℝ", bbN: "ℕ", bbZ: "ℤ", bbQ: "ℚ", bbC: "ℂ",
};

/* --- Werkzeug ---------------------------------------------------------------- */

/* Catmull-Rom: aus wenigen Stützpunkten eine weiche Kurve. */
export function glaette(p, teile = 4) {
  if (p.length < 6) return p;
  const n = p.length / 2;
  const P = (i) => {
    const j = Math.max(0, Math.min(n - 1, i));
    return [p[j * 2], p[j * 2 + 1]];
  };
  const aus = [p[0], p[1]];
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = P(i - 1);
    const [x1, y1] = P(i);
    const [x2, y2] = P(i + 1);
    const [x3, y3] = P(i + 2);
    for (let s = 1; s <= teile; s++) {
      const t = s / teile;
      const t2 = t * t;
      const t3 = t2 * t;
      const f = (a, b, c, d) =>
        0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
      aus.push(f(x0, x1, x2, x3), f(y0, y1, y2, y3));
    }
  }
  return aus;
}

const kreis = (cx, cy, rx, ry, start = 0, ende = Math.PI * 2, n = 20) => {
  const p = [];
  for (let i = 0; i <= n; i++) {
    const t = start + ((ende - start) * i) / n;
    p.push(cx + rx * Math.cos(t), cy + ry * Math.sin(t));
  }
  return p;
};

/* --- Gezeichnete Formen -------------------------------------------------------
   Jede Form: { w, striche: [{ p: [x,y,…], rund }] } in x-Höhen.
   A = Lage der Mittelachse, C = Ziffernhöhe — beides aus der Schrift. */

const FORMEN = {
  times: (A) => ({
    w: 0.62,
    striche: [
      { p: [0, A - 0.31, 0.62, A + 0.31] },
      { p: [0, A + 0.31, 0.62, A - 0.31] },
    ],
  }),
  equiv: (A) => ({
    w: 0.78,
    striche: [
      { p: [0, A + 0.3, 0.39, A + 0.31, 0.78, A + 0.3] },
      { p: [0, A, 0.39, A + 0.01, 0.78, A] },
      { p: [0, A - 0.3, 0.39, A - 0.29, 0.78, A - 0.3] },
    ],
  }),
  to: (A) => ({
    w: 1.05,
    striche: [
      { p: [0, A, 0.5, A + 0.02, 1.02, A] },
      { p: [0.76, A + 0.24, 1.04, A, 0.76, A - 0.24] },
    ],
  }),
  from: (A) => ({
    w: 1.05,
    striche: [
      { p: [0.03, A, 0.5, A + 0.02, 1.05, A] },
      { p: [0.29, A + 0.24, 0.01, A, 0.29, A - 0.24] },
    ],
  }),
  leftrightarrow: (A) => ({
    w: 1.25,
    striche: [
      { p: [0.03, A, 0.62, A + 0.02, 1.22, A] },
      { p: [0.29, A + 0.24, 0.01, A, 0.29, A - 0.24] },
      { p: [0.96, A + 0.24, 1.24, A, 0.96, A - 0.24] },
    ],
  }),
  Rightarrow: (A) => ({
    w: 1.1,
    striche: [
      { p: [0, A + 0.16, 0.5, A + 0.17, 0.92, A + 0.16] },
      { p: [0, A - 0.16, 0.5, A - 0.15, 0.92, A - 0.16] },
      { p: [0.72, A + 0.42, 1.1, A, 0.72, A - 0.42] },
    ],
  }),
  Leftarrow: (A) => ({
    w: 1.1,
    striche: [
      { p: [0.18, A + 0.16, 0.6, A + 0.17, 1.1, A + 0.16] },
      { p: [0.18, A - 0.16, 0.6, A - 0.15, 1.1, A - 0.16] },
      { p: [0.38, A + 0.42, 0, A, 0.38, A - 0.42] },
    ],
  }),
  Leftrightarrow: (A) => ({
    w: 1.35,
    striche: [
      { p: [0.18, A + 0.16, 0.68, A + 0.17, 1.17, A + 0.16] },
      { p: [0.18, A - 0.16, 0.68, A - 0.15, 1.17, A - 0.16] },
      { p: [0.38, A + 0.42, 0, A, 0.38, A - 0.42] },
      { p: [0.97, A + 0.42, 1.35, A, 0.97, A - 0.42] },
    ],
  }),
  in: (A) => ({
    w: 0.78,
    striche: [
      { p: [0.78, A + 0.45, 0.36, A + 0.46, 0.08, A + 0.26, 0.02, A, 0.08, A - 0.26, 0.36, A - 0.46, 0.78, A - 0.45], rund: true },
      { p: [0.05, A, 0.64, A] },
    ],
  }),
  notin: (A) => ({
    w: 0.78,
    striche: [
      { p: [0.78, A + 0.45, 0.36, A + 0.46, 0.08, A + 0.26, 0.02, A, 0.08, A - 0.26, 0.36, A - 0.46, 0.78, A - 0.45], rund: true },
      { p: [0.05, A, 0.64, A] },
      { p: [0.18, A - 0.7, 0.6, A + 0.7] },
    ],
  }),
  subset: (A) => ({
    w: 0.78,
    striche: [
      { p: [0.78, A + 0.45, 0.36, A + 0.46, 0.08, A + 0.26, 0.02, A, 0.08, A - 0.26, 0.36, A - 0.46, 0.78, A - 0.45], rund: true },
    ],
  }),
  subseteq: (A) => ({
    w: 0.78,
    striche: [
      { p: [0.78, A + 0.6, 0.36, A + 0.61, 0.08, A + 0.43, 0.02, A + 0.2, 0.08, A - 0.03, 0.36, A - 0.2, 0.78, A - 0.2], rund: true },
      { p: [0.02, A - 0.5, 0.78, A - 0.5] },
    ],
  }),
  cup: (A) => ({
    w: 0.84,
    striche: [
      { p: [0, A + 0.5, 0.02, A - 0.1, 0.18, A - 0.42, 0.42, A - 0.5, 0.66, A - 0.42, 0.82, A - 0.1, 0.84, A + 0.5], rund: true },
    ],
  }),
  cap: (A) => ({
    w: 0.84,
    striche: [
      { p: [0, A - 0.5, 0.02, A + 0.1, 0.18, A + 0.42, 0.42, A + 0.5, 0.66, A + 0.42, 0.82, A + 0.1, 0.84, A - 0.5], rund: true },
    ],
  }),
  perp: () => ({
    w: 0.95,
    striche: [
      { p: [0.47, 1.45, 0.475, 0.7, 0.47, 0.0] },
      { p: [0, 0, 0.47, 0.01, 0.95, 0] },
    ],
  }),
  parallel: () => ({
    w: 0.65,
    striche: [
      { p: [0.1, -0.1, 0.28, 1.45] },
      { p: [0.45, -0.1, 0.63, 1.45] },
    ],
  }),
  angle: () => ({
    w: 1.1,
    striche: [{ p: [0.92, 1.28, 0.02, 0.02, 1.1, 0.0] }],
  }),
  Delta: (A, C) => ({
    w: 1.12,
    striche: [{ p: [0.06, 0.03, 0.56, C, 1.1, 0.0, 0.0, 0.02] }],
  }),
  infty: (A) => {
    const p = [];
    const n = 32;
    for (let i = 0; i <= n; i++) {
      const t = (i / n) * Math.PI * 2;
      const s = Math.sin(t);
      const c = Math.cos(t);
      const q = 1 + s * s;
      p.push(0.64 + (0.64 * c) / q, A + (0.95 * s * c) / q);
    }
    return { w: 1.28, striche: [{ p }] };
  },
  pi: () => ({
    w: 1.08,
    striche: [
      { p: [-0.04, 0.95, 0.5, 1.0, 1.08, 1.04], rund: true },
      { p: [0.32, 1.0, 0.29, 0.5, 0.17, 0.0], rund: true },
      { p: [0.74, 1.0, 0.72, 0.45, 0.78, 0.08, 0.95, 0.06], rund: true },
    ],
  }),
  alpha: () => ({
    w: 1.05,
    striche: [
      {
        p: [0.98, 0.95, 0.78, 0.6, 0.57, 0.27, 0.38, 0.04, 0.17, 0.05, 0.03, 0.3, 0.05, 0.66, 0.22, 0.94, 0.43, 0.95, 0.6, 0.7, 0.74, 0.34, 0.86, 0.07, 1.04, 0.02],
        rund: true,
      },
    ],
  }),
  beta: () => ({
    w: 0.88,
    striche: [
      {
        p: [0.12, -0.5, 0.12, 0.6, 0.18, 1.2, 0.38, 1.48, 0.64, 1.42, 0.7, 1.12, 0.45, 0.88, 0.82, 0.62, 0.78, 0.18, 0.48, 0.0, 0.14, 0.12],
        rund: true,
      },
    ],
  }),
  gamma: () => ({
    w: 0.92,
    striche: [
      {
        p: [0.0, 0.82, 0.18, 0.92, 0.36, 0.45, 0.48, -0.08, 0.46, -0.4, 0.37, -0.48, 0.32, -0.3, 0.44, 0.02, 0.64, 0.46, 0.88, 0.95],
        rund: true,
      },
    ],
  }),
  delta: () => ({
    w: 0.9,
    striche: [
      {
        p: [0.78, 1.36, 0.48, 1.47, 0.2, 1.36, 0.28, 1.1, 0.64, 0.82, 0.86, 0.45, 0.72, 0.06, 0.42, 0.0, 0.12, 0.2, 0.15, 0.56, 0.48, 0.82],
        rund: true,
      },
    ],
  }),
  epsilon: () => ({
    w: 0.78,
    striche: [
      {
        p: [0.74, 0.86, 0.45, 0.98, 0.16, 0.86, 0.2, 0.6, 0.5, 0.52, 0.16, 0.44, 0.05, 0.16, 0.34, 0.0, 0.72, 0.1],
        rund: true,
      },
    ],
  }),
  theta: () => ({
    w: 0.8,
    striche: [{ p: kreis(0.4, 0.73, 0.36, 0.73, -Math.PI / 2, Math.PI * 1.55, 22) }, { p: [0.08, 0.73, 0.72, 0.74] }],
  }),
  lambda: () => ({
    w: 0.88,
    striche: [
      { p: [0.04, 1.42, 0.25, 1.4, 0.46, 0.9, 0.86, 0.0], rund: true },
      { p: [0.52, 0.72, 0.06, 0.0] },
    ],
  }),
  mu: () => ({
    w: 1.0,
    striche: [
      { p: [0.12, -0.48, 0.12, 0.4, 0.12, 0.96] },
      { p: [0.12, 0.32, 0.3, 0.02, 0.58, 0.08, 0.7, 0.96], rund: true },
      { p: [0.7, 0.96, 0.72, 0.22, 0.84, 0.0, 0.98, 0.06], rund: true },
    ],
  }),
  rho: () => ({
    w: 0.86,
    striche: [
      { p: [0.16, -0.5, 0.15, 0.45, 0.3, 0.9, 0.6, 0.96, 0.8, 0.62, 0.72, 0.16, 0.42, 0.0, 0.16, 0.2], rund: true },
    ],
  }),
  sigma: () => ({
    w: 1.05,
    striche: [
      { p: [1.05, 0.96, 0.56, 0.96, 0.2, 0.82, 0.05, 0.45, 0.2, 0.08, 0.5, 0.0, 0.76, 0.2, 0.8, 0.6, 0.56, 0.95], rund: true },
    ],
  }),
  tau: () => ({
    w: 0.95,
    striche: [
      { p: [0.0, 0.86, 0.45, 0.92, 0.92, 0.96], rund: true },
      { p: [0.5, 0.92, 0.44, 0.3, 0.52, 0.02, 0.72, 0.08], rund: true },
    ],
  }),
  phi: () => ({
    w: 0.98,
    striche: [{ p: kreis(0.49, 0.48, 0.45, 0.48, Math.PI * 0.5, Math.PI * 2.5, 22) }, { p: [0.47, -0.5, 0.49, 0.5, 0.52, 1.45] }],
  }),
  omega: () => ({
    w: 1.1,
    striche: [
      { p: [0.18, 0.92, 0.02, 0.46, 0.16, 0.03, 0.38, 0.05, 0.52, 0.48, 0.66, 0.05, 0.9, 0.03, 1.06, 0.46, 0.9, 0.92], rund: true },
    ],
  }),
  Omega: (A, C) => ({
    w: 1.12,
    striche: [
      {
        p: [0.04, 0.0, 0.36, 0.0, 0.38, 0.16, 0.12, 0.5 * C / 1.45, 0.1, C * 0.68, 0.34, C, 0.78, C, 1.02, C * 0.68, 1.0, 0.5 * C / 1.45, 0.74, 0.16, 0.76, 0.0, 1.08, 0.0],
        rund: true,
      },
    ],
  }),
  Sigma: (A, C) => ({
    w: 1.0,
    striche: [{ p: [1.0, C, 0.04, C, 0.62, C / 2, 0.04, 0.0, 1.02, 0.02] }],
  }),
  /* Ersatz, wenn selbst die zusammengesetzten Zeichen keine Schrift finden */
  cdot: (A) => ({ w: 0.22, striche: [{ p: [0.11, A] }] }),
  deg: (A, C) => ({ w: 0.42, striche: [{ p: kreis(0.21, C - 0.2, 0.17, 0.2, 0, Math.PI * 2, 14) }] }),
  prime: (A, C) => ({ w: 0.28, striche: [{ p: [0.24, C + 0.08, 0.06, C - 0.45] }] }),
  pm: (A) => ({
    w: 0.75,
    striche: [
      { p: [0.375, A + 0.62, 0.375, A - 0.12] },
      { p: [0, A + 0.25, 0.75, A + 0.25] },
      { p: [0, A - 0.45, 0.75, A - 0.45] },
    ],
  }),
  mp: (A) => ({
    w: 0.75,
    striche: [
      { p: [0.375, A + 0.12, 0.375, A - 0.62] },
      { p: [0, A - 0.25, 0.75, A - 0.25] },
      { p: [0, A + 0.45, 0.75, A + 0.45] },
    ],
  }),
  le: (A) => ({
    w: 0.75,
    striche: [{ p: [0.75, A + 0.5, 0.02, A + 0.15, 0.75, A - 0.2] }, { p: [0.02, A - 0.48, 0.75, A - 0.48] }],
  }),
  ge: (A) => ({
    w: 0.75,
    striche: [{ p: [0, A + 0.5, 0.73, A + 0.15, 0, A - 0.2] }, { p: [0, A - 0.48, 0.73, A - 0.48] }],
  }),
  ne: (A) => ({
    w: 0.75,
    striche: [
      { p: [0, A + 0.2, 0.75, A + 0.2] },
      { p: [0, A - 0.2, 0.75, A - 0.2] },
      { p: [0.2, A - 0.6, 0.55, A + 0.6] },
    ],
  }),
  approx: (A) => ({
    w: 0.8,
    striche: [
      { p: [0, A + 0.12, 0.2, A + 0.32, 0.4, A + 0.2, 0.6, A + 0.12, 0.8, A + 0.3], rund: true },
      { p: [0, A - 0.28, 0.2, A - 0.08, 0.4, A - 0.2, 0.6, A - 0.28, 0.8, A - 0.1], rund: true },
    ],
  }),
  cong: (A) => ({
    w: 0.8,
    striche: [
      { p: [0, A + 0.32, 0.2, A + 0.5, 0.4, A + 0.38, 0.6, A + 0.3, 0.8, A + 0.48], rund: true },
      { p: [0, A, 0.8, A] },
      { p: [0, A - 0.3, 0.8, A - 0.3] },
    ],
  }),
  empty: (A, C) => ({
    w: 0.85,
    striche: [{ p: kreis(0.42, C / 2, 0.34, C / 2, 0, Math.PI * 2, 20) }, { p: [0.0, -0.15, 0.85, C + 0.15] }],
  }),
};

/* Doppelstrich-Buchstaben ℝ ℕ ℤ ℚ ℂ: Buchstabe der Schrift plus ein zweiter
   Strich, wie man sie an der Tafel schreibt. */
function doppelStrich(buchstabe, hilfe) {
  const b = hilfe.glyphBox(buchstabe);
  if (!b) return null;
  const { links, rechts, oben, unten } = b.tinte;
  const w = rechts - links;
  const h = oben - unten;
  let p;
  if (buchstabe === "Z") {
    p = [links + w * 0.82, oben - h * 0.05, links + w * 0.3, unten + h * 0.05];
  } else if (buchstabe === "C" || buchstabe === "Q") {
    p = [links + w * 0.24, unten + h * 0.18, links + w * 0.24, oben - h * 0.18];
  } else {
    p = [links + w * 0.26, unten + h * 0.02, links + w * 0.26, oben - h * 0.02];
  }
  return { ...b, striche: [...b.striche, { p, d: hilfe.d }] };
}

/* Ein Zeichen der Schrift, verschoben und mit zusätzlichen Strichen. */
function zusammen(hilfe, ch, k, mitteBei, extra) {
  const b = hilfe.glyphBox(ch, k);
  if (!b) return null;
  const { u } = hilfe;
  const t = b.tinte;
  const dy = mitteBei * u - (t.oben + t.unten) / 2;
  const verschoben = hilfe.verschiebe(b, 0, dy);
  const tinte = { links: t.links, rechts: t.rechts, oben: t.oben + dy, unten: t.unten + dy };
  const striche = extra ? extra(tinte) : [];
  return hilfe.mitStrichen(verschoben, striche);
}

/* Baut ein Symbol als Kasten. `hilfe` kommt aus layout.js:
   { u, A, C, d, glyphBox(ch, k?), verschiebe, mitStrichen, ausForm }.
   A und C sind dort schon in x-Höhen umgerechnet. */
export function baueSymbol(name, hilfe) {
  const { u, A, d } = hilfe;
  const linie = (y, t, ueber = 0.08) => ({
    p: [t.links - ueber * u, y, (t.links + t.rechts) / 2, y + 0.01 * u, t.rechts + ueber * u, y],
    d,
  });

  switch (name) {
    case "cdot": {
      const b = zusammen(hilfe, ".", 1, A, null);
      if (b) return hilfe.mitRand(b, 0.1);
      break;
    }
    case "deg": {
      const o = hilfe.glyphBox("o", 0.55);
      if (o) {
        const dy = hilfe.C * u - o.tinte.oben;
        return hilfe.mitRand(hilfe.verschiebe(o, 0, dy), 0.04);
      }
      break;
    }
    case "prime": {
      if (hilfe.hat("'")) {
        const b = hilfe.glyphBox("'");
        if (b) return b;
      }
      break;
    }
    case "pm":
    case "mp": {
      const oben = name === "pm";
      const b = zusammen(hilfe, "+", 0.9, A + (oben ? 0.2 : -0.2), (t) => [
        linie(oben ? t.unten - 0.24 * u : t.oben + 0.24 * u, t, 0),
      ]);
      if (b) return hilfe.mitRand(b, 0.08);
      break;
    }
    case "le":
    case "ge": {
      const b = zusammen(hilfe, name === "le" ? "<" : ">", 0.9, A + 0.18, (t) => [
        linie(t.unten - 0.22 * u, t, 0),
      ]);
      if (b) return hilfe.mitRand(b, 0.04);
      break;
    }
    case "ne": {
      const b = zusammen(hilfe, "=", 1, A, (t) => {
        const w = t.rechts - t.links;
        return [{ p: [t.links + w * 0.3, A * u - 0.62 * u, t.rechts - w * 0.3, A * u + 0.62 * u], d }];
      });
      if (b) return b;
      break;
    }
    case "approx": {
      const a = hilfe.glyphBox("~", 0.95);
      if (a) {
        const t = a.tinte;
        const mitte = (t.oben + t.unten) / 2;
        const oben = hilfe.verschiebe(a, 0, (A + 0.2) * u - mitte);
        const unten = hilfe.verschiebe(a, 0, (A - 0.2) * u - mitte);
        return hilfe.vereine([oben, unten]);
      }
      break;
    }
    case "cong": {
      const a = hilfe.glyphBox("~", 0.85);
      const gl = hilfe.glyphBox("=", 0.85);
      if (a && gl) {
        const oben = hilfe.verschiebe(a, 0, (A + 0.38) * u - (a.tinte.oben + a.tinte.unten) / 2);
        const unten = hilfe.verschiebe(gl, 0, (A - 0.12) * u - (gl.tinte.oben + gl.tinte.unten) / 2);
        return hilfe.vereine([oben, unten]);
      }
      break;
    }
    case "empty": {
      const b = hilfe.glyphBox("0");
      if (b) {
        const t = b.tinte;
        return hilfe.mitStrichen(b, [
          { p: [t.links - 0.12 * u, t.unten - 0.15 * u, t.rechts + 0.12 * u, t.oben + 0.15 * u], d },
        ]);
      }
      break;
    }
    case "bbR":
    case "bbN":
    case "bbZ":
    case "bbQ":
    case "bbC": {
      const b = doppelStrich(name.slice(2), hilfe);
      if (b) return b;
      break;
    }
    default:
      break;
  }

  const form = FORMEN[name];
  if (!form) return null;
  return hilfe.ausForm(form(hilfe.A, hilfe.C));
}
